/* eslint-disable react/prop-types, jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
// Twilio Agent admin settings — a wide modal with a left section nav and a right
// content pane. Super-user only (the caller gates the trigger; the goserver
// enforces every mutation). Two sections today: Teams and Routing. Teams and
// agents are loaded once here and shared with both sections so a team created in
// Teams is immediately selectable in Routing. Escape and overlay-click close.

import React, { useCallback, useEffect, useState } from 'react';
import { FaUsers, FaRoute } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import TeamsSettings from './TeamsSettings';
import RoutingSettings from './RoutingSettings';
import { fetchTeams, fetchAgents, fetchPhoneNumbers, fetchCallerTypes } from './settings_api';
import './twilio_agent_settings.css';

const SETTINGS_SECTIONS = [
  { key: 'teams', label: 'Teams', icon: FaUsers },
  { key: 'routing', label: 'Routing', icon: FaRoute }
];

export default function SettingsModal({ onClose }) {
  const [section, setSection] = useState('teams');
  const [teams, setTeams] = useState([]);
  const [agents, setAgents] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [callerTypes, setCallerTypes] = useState([]);
  const [teamsStatus, setTeamsStatus] = useState('loading');

  const reloadTeams = useCallback(() => {
    setTeamsStatus((s) => (s === 'success' ? s : 'loading'));
    return fetchTeams()
      .then((rows) => {
        setTeams(rows);
        setTeamsStatus('success');
        return rows;
      })
      .catch(() => setTeamsStatus('fail'));
  }, []);

  useEffect(() => {
    reloadTeams();
    fetchAgents()
      .then(setAgents)
      .catch(() => setAgents([]));
    fetchPhoneNumbers()
      .then(setPhoneNumbers)
      .catch(() => setPhoneNumbers([]));
    fetchCallerTypes()
      .then(setCallerTypes)
      .catch(() => setCallerTypes([]));
  }, [reloadTeams]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="ta-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="ta-modal ta-settings-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Twilio settings"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="ta-modal-close" aria-label="Close" onClick={onClose}>
          <MdClose size={20} />
        </button>
        <h2 className="ta-modal-title">Twilio Settings</h2>

        <div className="ta-settings-body">
          <div className="ta-settings-nav">
            {SETTINGS_SECTIONS.map((s) => {
              const IconEl = s.icon;
              return (
                <button
                  key={s.key}
                  type="button"
                  className={`ta-settings-nav-item ${section === s.key ? 'ta-active' : ''}`}
                  onClick={() => setSection(s.key)}
                >
                  <IconEl size={15} />
                  {s.label}
                </button>
              );
            })}
          </div>
          <div className="ta-settings-content">
            {section === 'teams' && (
              <TeamsSettings
                teams={teams}
                agents={agents}
                status={teamsStatus}
                onReload={reloadTeams}
              />
            )}
            {section === 'routing' && (
              <RoutingSettings
                teams={teams}
                teamsStatus={teamsStatus}
                phoneNumbers={phoneNumbers}
                callerTypes={callerTypes}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
